import { useEffect, useRef } from "react";

/*
  Brand intro splash — the panel-split wordmark reveal.
  Shared by every entry page and shown only once per browser session so that
  moving between the mobile-app page and the platform page never replays it.
*/

const INTRO_SEEN_KEY = "sm-intro-seen";

export function hasSeenIntro() {
  try {
    return sessionStorage.getItem(INTRO_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markIntroSeen() {
  try {
    sessionStorage.setItem(INTRO_SEEN_KEY, "1");
  } catch {
    /* private mode — intro simply replays */
  }
}

/* Intro wordmark: anchor spacing = glyph width + fixed gap (calibrated to n→t) */
const INTRO_NAME_CHARS = [
  { ch: "S" }, { ch: "e" }, { ch: "n" }, { ch: "t" }, { ch: "i" }, { ch: "o" },
  { ch: "M", wordGapBefore: true }, { ch: "i" }, { ch: "n" }, { ch: "d" },
];
const INTRO_GLYPH_WIDTH = { S: 39, e: 30, n: 40, t: 24, i: 14, o: 38, M: 64, d: 42 };
const INTRO_LETTER_GAP = 4;
const INTRO_WORD_GAP = 10;

function buildIntroNameLayout() {
  let x = 0;
  const letters = INTRO_NAME_CHARS.map((item, idx) => {
    if (item.wordGapBefore) x += INTRO_WORD_GAP;
    const letter = { ch: item.ch, x, idx };
    x += (INTRO_GLYPH_WIDTH[item.ch] ?? 40) + INTRO_LETTER_GAP;
    return letter;
  });
  const textWidth = x - INTRO_LETTER_GAP;
  const pad = 20;
  return { letters, viewBoxWidth: Math.ceil(textWidth + pad * 2), offsetX: pad };
}

const INTRO_NAME_LAYOUT = buildIntroNameLayout();

const introStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,300&family=Outfit:wght@200;300&display=swap');

  #sentio-intro {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }
  .intro-panel {
    flex: 1;
    background: #0fa5a5;
    position: relative;
    overflow: hidden;
    transition: transform 1.1s cubic-bezier(0.76, 0, 0.24, 1);
  }
  .intro-panel-top    { transform-origin: top center; }
  .intro-panel-bottom { transform-origin: bottom center; }

  #sentio-intro.revealed .intro-panel-top    { transform: translateY(-100%); }
  #sentio-intro.revealed .intro-panel-bottom { transform: translateY(100%); }

  #sentio-brand {
    --intro-text-tracking: 0.3em;
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    gap: 0;
    text-align: center;
  }

  @keyframes fadeInSub {
    from { opacity: 0; letter-spacing: 0.5em; }
    to   { opacity: 1; letter-spacing: var(--intro-text-tracking); }
  }
  @keyframes fadeOutSub {
    from { opacity: 1; }
    to   { opacity: 0; }
  }

  .intro-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-weight: 200;
    font-size: clamp(12px, 1.3vw, 15px);
    letter-spacing: var(--intro-text-tracking);
    color: rgba(255,255,255,0.92);
    text-transform: uppercase;
    margin-bottom: 1.1rem;
    opacity: 0;
  }
  .intro-eyebrow.vis  { animation: fadeInSub 0.9s ease forwards; }
  .intro-eyebrow.hide { animation: fadeOutSub 0.4s ease forwards; }

  .intro-line {
    width: 0;
    height: 1px;
    background: rgba(255,255,255,0.3);
    margin: 1.5rem 0;
    transition: width 0.6s ease, opacity 0.6s ease;
    opacity: 0;
  }
  .intro-line.vis  { width: 220px; opacity: 1; }
  .intro-line.hide { opacity: 0; }

  .intro-sub {
    font-family: 'Outfit', sans-serif;
    font-weight: 200;
    font-size: clamp(10px, 1vw, 12px);
    letter-spacing: var(--intro-text-tracking);
    color: rgba(255,255,255,0.45);
    text-transform: uppercase;
    opacity: 0;
  }
  .intro-sub.vis  { animation: fadeInSub 0.8s ease forwards; }
  .intro-sub.hide { animation: fadeOutSub 0.4s ease forwards; }

  .intro-name {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 100vw;
    margin: 0 auto;
  }

  .letter-path { opacity: 0; fill-opacity: 0; stroke-opacity: 0; }

  @keyframes letterDraw {
    0%   { opacity: 1; fill-opacity: 0; stroke-opacity: 1; stroke-dashoffset: var(--dash-len); }
    70%  { opacity: 1; fill-opacity: 0; stroke-opacity: 1; stroke-dashoffset: 0; }
    100% { opacity: 1; fill-opacity: 1; stroke-opacity: 0; stroke-dashoffset: 0; }
  }
  @keyframes letterErase {
    from { opacity: 1; fill-opacity: 1; stroke-opacity: 0; stroke-dashoffset: 0; }
    to   { opacity: 0; fill-opacity: 0; stroke-opacity: 0; }
  }

  .intro-name.vis .letter-path {
    stroke-dasharray: var(--dash-len);
    animation: letterDraw 1.1s ease forwards;
    animation-delay: var(--letter-delay);
  }
  .intro-name.hide .letter-path {
    stroke-dasharray: var(--dash-len);
    stroke-dashoffset: 0;
    fill-opacity: 1;
    stroke-opacity: 0;
    opacity: 1;
    animation: letterErase 0.35s ease forwards;
    animation-delay: var(--letter-erase-delay);
  }

  .lp-0 { --dash-len: 260px; --letter-delay: 0.00s; --letter-erase-delay: 0.00s; }
  .lp-1 { --dash-len: 200px; --letter-delay: 0.22s; --letter-erase-delay: 0.05s; }
  .lp-2 { --dash-len: 210px; --letter-delay: 0.44s; --letter-erase-delay: 0.10s; }
  .lp-3 { --dash-len: 160px; --letter-delay: 0.66s; --letter-erase-delay: 0.15s; }
  .lp-4 { --dash-len: 100px; --letter-delay: 0.88s; --letter-erase-delay: 0.20s; }
  .lp-5 { --dash-len: 210px; --letter-delay: 1.10s; --letter-erase-delay: 0.25s; }
  .lp-6 { --dash-len: 280px; --letter-delay: 1.32s; --letter-erase-delay: 0.30s; }
  .lp-7 { --dash-len: 100px; --letter-delay: 1.54s; --letter-erase-delay: 0.35s; }
  .lp-8 { --dash-len: 210px; --letter-delay: 1.76s; --letter-erase-delay: 0.40s; }
  .lp-9 { --dash-len: 220px; --letter-delay: 1.98s; --letter-erase-delay: 0.45s; }
`;

export default function BrandIntro({ onDone }) {
  const introRef = useRef(null);
  const brandRef = useRef(null);
  const eyebrowRef = useRef(null);
  const nameRef = useRef(null);
  const lineRef = useRef(null);
  const subRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    markIntroSeen();

    const timers = [];
    const t = (fn, ms) => timers.push(setTimeout(fn, ms));
    const show = (ref) => ref.current && ref.current.classList.add("vis");
    const hide = (ref) => ref.current && ref.current.classList.replace("vis", "hide");

    // Draw in — eyebrow first, then letters, then line/sub
    t(() => show(eyebrowRef), 300);
    t(() => show(nameRef), 500);
    t(() => show(lineRef), 700);
    t(() => show(subRef), 900);

    // Last letter finishes drawing at ~500 + 1980 + 1100 = ~3580ms.
    // Hold visible for a beat, then erase from 4000ms.
    t(() => hide(subRef), 4000);
    t(() => hide(lineRef), 4200);
    t(() => hide(nameRef), 4400);
    t(() => hide(eyebrowRef), 4600);

    // Guarantee the brand is fully invisible before the panels move
    t(() => { if (brandRef.current) brandRef.current.style.opacity = "0"; }, 5200);
    t(() => introRef.current && introRef.current.classList.add("revealed"), 5300);
    t(() => onDoneRef.current?.(), 6500);

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <style>{introStyle}</style>

      <div id="sentio-intro" ref={introRef}>
        <div className="intro-panel intro-panel-top" />
        <div className="intro-panel intro-panel-bottom" />
      </div>

      <div id="sentio-brand" ref={brandRef}>
        <p className="intro-eyebrow" ref={eyebrowRef}>Welcome to</p>

        <div className="intro-name" ref={nameRef}>
          <svg
            viewBox={`0 0 ${INTRO_NAME_LAYOUT.viewBoxWidth} 100`}
            xmlns="http://www.w3.org/2000/svg"
            width={INTRO_NAME_LAYOUT.viewBoxWidth}
            style={{ maxWidth: `min(90vw, ${INTRO_NAME_LAYOUT.viewBoxWidth}px)`, display: "block" }}
            aria-label="Sentio Mind"
          >
            <g transform={`translate(${INTRO_NAME_LAYOUT.offsetX}, 0)`}>
              {INTRO_NAME_LAYOUT.letters.map((item) => (
                <text
                  key={item.idx}
                  className={`letter-path lp-${item.idx}`}
                  x={item.x}
                  y="82"
                  fontFamily="'Cormorant Garamond', serif"
                  fontWeight="300"
                  fontSize="88"
                  fill="white"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  paintOrder="stroke"
                >
                  {item.ch}
                </text>
              ))}
            </g>
          </svg>
        </div>

        <div className="intro-line" ref={lineRef} />
        <p className="intro-sub" ref={subRef}>From patterns to prevention.</p>
      </div>
    </>
  );
}
